import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, X, Box, Link2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { objectsApi, type ImportResult } from '../api'

interface ObjectImportDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectService: 'Object' | 'Service'
}

type ImportMode = 'objects' | 'mapping'
type ImportState = 'idle' | 'uploading' | 'complete'

interface MappingImportResult {
    totalInFile: number
    imported: number
    alreadyExisting: number
    errors: Array<{ row: number; error: string; sourceID?: string; targetID?: string }>
}

export function ObjectImportDialog({ open, onOpenChange, objectService }: ObjectImportDialogProps) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [mode, setMode] = useState<ImportMode>('objects')
    const [file, setFile] = useState<File | null>(null)
    const [dragOver, setDragOver] = useState(false)
    const [state, setState] = useState<ImportState>('idle')
    const [progress, setProgress] = useState(0)
    const [result, setResult] = useState<ImportResult | MappingImportResult | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [scenarioId, setScenarioId] = useState<string>('')
    const [scenarios, setScenarios] = useState<Array<{ id: number; name: string }>>([])

    // Load scenarios when mapping mode is selected
    useEffect(() => {
        if (mode === 'mapping' && scenarios.length === 0) {
            objectsApi.getAllScenarios().then(res => {
                const data = (res.data as any)?.data || res.data
                if (Array.isArray(data)) {
                    setScenarios(data)
                    const defaultScenario = data.find((s: any) => s.isDefault)
                    if (defaultScenario) setScenarioId(String(defaultScenario.id))
                }
            }).catch(() => {})
        }
    }, [mode, scenarios.length])

    const reset = useCallback(() => {
        setFile(null)
        setState('idle')
        setProgress(0)
        setResult(null)
        setError(null)
    }, [])

    const handleClose = useCallback((open: boolean) => {
        if (!open) reset()
        onOpenChange(open)
    }, [onOpenChange, reset])

    const handleModeChange = useCallback((newMode: ImportMode) => {
        setMode(newMode)
        reset()
    }, [reset])

    const handleFileSelect = useCallback((selectedFile: File) => {
        const valid = /\.(csv|xlsx|xls)$/i.test(selectedFile.name)
        if (!valid) {
            setError(t('objects.import.invalidFileType'))
            return
        }
        setFile(selectedFile)
        setError(null)
    }, [t])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setDragOver(false)
        const droppedFile = e.dataTransfer.files[0]
        if (droppedFile) handleFileSelect(droppedFile)
    }, [handleFileSelect])

    const handleImport = useCallback(async () => {
        if (!file) return
        if (mode === 'mapping' && !scenarioId) {
            setError(t('objects.import.selectScenario'))
            return
        }
        setState('uploading')
        setProgress(10)
        setError(null)

        // Simulate progress during upload
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) { clearInterval(progressInterval); return 90 }
                return prev + Math.random() * 15
            })
        }, 300)

        try {
            let data: any
            if (mode === 'objects') {
                const response = await objectsApi.importObjects(file, objectService)
                data = (response.data as any)?.data || response.data
                queryClient.invalidateQueries({ queryKey: ['objects'] })
            } else {
                const response = await objectsApi.importMapping(file, parseInt(scenarioId, 10))
                data = (response.data as any)?.data || response.data
            }
            clearInterval(progressInterval)
            setProgress(100)
            setResult(data)
            setState('complete')
        } catch (err: any) {
            clearInterval(progressInterval)
            setProgress(0)
            const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Import failed'
            setError(msg)
            setState('idle')
        }
    }, [file, mode, objectService, scenarioId, queryClient, t])

    const isMapping = mode === 'mapping'
    const canImport = file && state !== 'uploading' && (mode === 'objects' || scenarioId)

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t('objects.import.title')}</DialogTitle>
                </DialogHeader>

                {/* Mode toggle */}
                <div className="flex rounded-lg border p-0.5 bg-muted/30 gap-1">
                    <button
                        type="button"
                        onClick={() => handleModeChange('objects')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'objects' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <Box className="h-4 w-4" />
                        {t('objects.import.importObjects')}
                    </button>
                    <button
                        type="button"
                        onClick={() => handleModeChange('mapping')}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            mode === 'mapping' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        <Link2 className="h-4 w-4" />
                        {t('objects.import.importMapping')}
                    </button>
                </div>

                {state === 'complete' && result ? (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-medium">{t('objects.import.complete')}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="rounded-lg border bg-muted/30 p-3">
                                <p className="text-2xl font-bold">{result.totalInFile}</p>
                                <p className="text-xs text-muted-foreground">{t('objects.import.inFile')}</p>
                            </div>
                            <div className="rounded-lg border bg-green-50 dark:bg-green-950/20 p-3">
                                <p className="text-2xl font-bold text-green-600">{result.imported}</p>
                                <p className="text-xs text-muted-foreground">{t('objects.import.imported')}</p>
                            </div>
                            <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 p-3">
                                <p className="text-2xl font-bold text-amber-600">{result.alreadyExisting}</p>
                                <p className="text-xs text-muted-foreground">{t('objects.import.existing')}</p>
                            </div>
                        </div>
                        {result.errors.length > 0 && (
                            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-3 max-h-40 overflow-y-auto">
                                <p className="text-sm font-medium text-red-600 mb-1">
                                    {t('objects.import.errors', { count: result.errors.length })}
                                </p>
                                <ul className="text-xs text-red-600 space-y-0.5">
                                    {result.errors.slice(0, 20).map((err, i) => (
                                        <li key={i}>
                                            {t('objects.import.rowError', { row: err.row })}: {err.error}
                                        </li>
                                    ))}
                                    {result.errors.length > 20 && (
                                        <li className="font-medium">...{t('objects.import.moreErrors', { count: result.errors.length - 20 })}</li>
                                    )}
                                </ul>
                            </div>
                        )}
                        <DialogFooter>
                            <Button onClick={() => handleClose(false)}>{t('common.close')}</Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {isMapping ? t('objects.import.mappingDescription') : t('objects.import.description')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {t('objects.import.requiredColumns')}: {isMapping ? (
                                <><strong>SourceID</strong>, <strong>TargetID</strong></>
                            ) : (
                                <><strong>ObjectID</strong>, <strong>Description</strong>, <strong>Layer</strong></>
                            )}
                        </p>
                        {!isMapping && (
                            <p className="text-xs text-muted-foreground">
                                {t('objects.import.objectIdRules')}
                            </p>
                        )}

                        {/* Scenario selector for mapping mode */}
                        {isMapping && (
                            <div>
                                <label className="text-sm font-medium mb-1 block">{t('objects.import.scenario')}</label>
                                <Select value={scenarioId} onValueChange={setScenarioId}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={t('objects.import.selectScenario')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {scenarios.map(s => (
                                            <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div
                            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                                ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50'}`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => {
                                    const f = e.target.files?.[0]
                                    if (f) handleFileSelect(f)
                                    e.target.value = ''
                                }}
                            />
                            {file ? (
                                <div className="flex items-center justify-center gap-3">
                                    <FileSpreadsheet className="h-8 w-8 text-green-600" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium">{file.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={(e) => { e.stopPropagation(); setFile(null) }}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">
                                        {t('objects.import.dropzone')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">CSV, XLSX, XLS</p>
                                </div>
                            )}
                        </div>

                        {/* Progress bar */}
                        {state === 'uploading' && (
                            <div className="space-y-1">
                                <Progress value={progress} className="h-2" />
                                <p className="text-xs text-muted-foreground text-center">
                                    {t('objects.import.importing')}... {Math.round(progress)}%
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="flex items-center gap-2 text-sm text-red-600">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => handleClose(false)}>
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={handleImport}
                                disabled={!canImport}
                            >
                                {state === 'uploading' ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        {t('objects.import.importing')}
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-4 w-4 mr-2" />
                                        {t('objects.import.importButton')}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
